import * as S from "./styles";
import { SearchableSelectInput } from "../../../../../../components/New/SearchableSelectInput";
import StepIndicator from "../StepIndicator/StepIndicator";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Tag } from "../../../../../../@types/Tags";
import useTags from "../../../../../../hooks/api/Inventory/useTags";
import useAutoPrintLinesCount from "../../../../../../hooks/api/Inventory/useAutoPrintLinesCount";
import useCreateAutoPrint from "../../../../../../hooks/api/Inventory/useCreateAutoPrint";
import useAutoPrintCSVDownload from "../../../../../../hooks/api/Inventory/useAutoPrintCSVDownload";
import { Button } from "../../../../../../components/New/Button";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { useTranslateConstants } from "../../hooks/useTranslateConstants";

interface AutoPrintModalProps {
  isResponsive?: boolean;
  setCloseDrawer?: () => void;
  onSuccess?: () => void;
}

export function AutoPrintModal({
  isResponsive,
  setCloseDrawer,
  onSuccess,
}: AutoPrintModalProps) {
  const { t } = useTranslation();

  const { STEPS, STEP_LABELS, TEXTS } = useTranslateConstants();

  const [currentStep, setCurrentStep] = useState<number>(STEPS.TAG_SELECTION);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  const { tags, loading } = useTags();
  const {
    totalLines,
    loading: loadingCount,
    fetchLinesCount,
  } = useAutoPrintLinesCount();
  const {
    createAutoPrint,
    loading: createLoading,
    error,
  } = useCreateAutoPrint();
  const { loading: csvLoading, downloadCSV } = useAutoPrintCSVDownload();

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handlePrint = async () => {
    if (!selectedTag) return;

    const success = await createAutoPrint([selectedTag.name]);

    if (success) {
      setCurrentStep(STEPS.SUCCESS);
      onSuccess?.();
    }
  };

  const handleNext = () => setCurrentStep(currentStep + 1);

  const handleBack = () => setCurrentStep(currentStep - 1);

  const handleCSVDownload = async () => {
    if (!selectedTag) return;

    toast.promise(downloadCSV(selectedTag.name), {
      pending: t("downloadingCsv"),
      success: t("csvDownloadedSuccessfully"),
      error: {
        render({ data }: { data: { message: string } }) {
          return data?.message || t("failedToDownloadCsv");
        },
      },
    });
  };

  const handleActionClick = () => {
    if (currentStep === STEPS.CONFIRMATION) {
      handlePrint();
    } else if (currentStep === STEPS.TAG_SELECTION) {
      if (!selectedTag) return;
      fetchLinesCount(selectedTag?.name);
      handleNext();
    } else {
      handleNext();
    }
  };

  const isButtonDisabled =
    (currentStep === STEPS.TAG_SELECTION && !selectedTag) ||
    (currentStep === STEPS.SUMMARY && (loadingCount || totalLines === 0)) ||
    createLoading;

  const getButtonText = () => {
    if (currentStep < STEPS.CONFIRMATION) return TEXTS.NEXT;
    return TEXTS.PRINT;
  };

  const getCancelText = () => {
    return currentStep === STEPS.SUCCESS ? TEXTS.CLOSE : TEXTS.CANCEL;
  };

  const isCSVDownloadDisabled = csvLoading || !selectedTag || totalLines === 0;

  const shouldShowBackButton =
    currentStep > STEPS.TAG_SELECTION && currentStep !== STEPS.SUCCESS;

  return (
    <S.Content>
      <S.StepsContainer>
        <StepIndicator
          steps={Object.values(STEP_LABELS)}
          activeStep={currentStep}
        />
      </S.StepsContainer>

      {currentStep === STEPS.TAG_SELECTION && (
        <SearchableSelectInput
          placeholder={loading ? TEXTS.LOADING_TAGS : TEXTS.CHOOSE_TAG}
          options={tags}
          onOptionSelect={(tag: Tag) => setSelectedTag(tag)}
          disabled={loading}
          initialValue={selectedTag || undefined}
        />
      )}

      {currentStep === STEPS.SUMMARY && (
        <S.Step2Container>
          <S.TotalResultsContainer>
            <span>{TEXTS.TOTAL_RESULTS}</span>
            <p>
              {loadingCount
                ? TEXTS.LOADING
                : t("linesCount", { count: totalLines })}
            </p>
          </S.TotalResultsContainer>

          <S.CSVContainer>
            <p>
              <button
                onClick={handleCSVDownload}
                disabled={isCSVDownloadDisabled}
              >
                {t("clickHere")}
              </button>
              &nbsp;{t("toDownloadCsv")}
            </p>
          </S.CSVContainer>
        </S.Step2Container>
      )}

      {currentStep === STEPS.CONFIRMATION && selectedTag && (
        <S.Step3Container>
          {t("confirmationMessage", {
            count: totalLines,
            tagName: selectedTag.name,
          })}
        </S.Step3Container>
      )}

      {currentStep === STEPS.SUCCESS && (
        <S.Step4Container>
          <S.PreLineText>{TEXTS.SUCCESS_MESSAGE}</S.PreLineText>
        </S.Step4Container>
      )}

      {isResponsive ? (
        <S.ResponsiveButtonContainer>
          {shouldShowBackButton && (
            <S.BackButton>
              <Button variant="ghost" title={t("back")} onClick={handleBack} />
            </S.BackButton>
          )}

          <Button
            variant="ghost"
            title={getCancelText()}
            onClick={setCloseDrawer}
          />

          {currentStep !== STEPS.SUCCESS && (
            <Button
              title={getButtonText()}
              onClick={handleActionClick}
              disabled={isButtonDisabled}
              isLoading={createLoading}
            />
          )}
        </S.ResponsiveButtonContainer>
      ) : (
        <S.ButtonContainer>
          {shouldShowBackButton && (
            <S.BackButton>
              <Button variant="ghost" title={t("back")} onClick={handleBack} />
            </S.BackButton>
          )}
          <S.ButtonGroup>
            <Dialog.Close asChild>
              <Button
                variant="ghost"
                title={getCancelText()}
                onClick={setCloseDrawer}
              />
            </Dialog.Close>

            {currentStep !== STEPS.SUCCESS && (
              <Button
                title={getButtonText()}
                onClick={handleActionClick}
                disabled={isButtonDisabled}
                isLoading={createLoading}
              />
            )}
          </S.ButtonGroup>
        </S.ButtonContainer>
      )}
    </S.Content>
  );
}
